// src/pages/Contact.tsx
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { PageHero } from '@/components/PageHero';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  MessageCircle,
  Send,
  CheckCircle2,
  AlertCircle,
  Users,
  Camera,
  Handshake,
  HeadphonesIcon,
  UserPlus,
  Navigation,
  Building2,
  Globe,
  Zap,
  Shield,
  Calendar,
  FileText,
  Download,
  ExternalLink,
  ChevronRight,
  Star,
  TrendingUp,
  Award,
  Target,
  Heart,
  Sparkles,
  Search,
  Filter
} from 'lucide-react';
import {
  contactMethods,
  branches,
  departments,
  faqs,
  contactSubjects,
  emergencyContact,
  contactStats,
  type Branch,
  type FAQ
} from '@/data/mock/contact';
import { socialLinks } from '@/config/socialLinks';

const Contact = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const isRTL = i18n.language === 'ar';

  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    branch: '',
    message: '',
    newsletter: false
  });

  // Filter state
  const [selectedBranch, setSelectedBranch] = useState<string>('all');
  const [faqCategory, setFaqCategory] = useState<string>('all');
  const [faqSearch, setFaqSearch] = useState('');

  // Icon mapping
  const iconMap: Record<string, any> = {
    Phone, Mail, MessageCircle, MapPin, Clock, Users, Camera,
    Handshake, HeadphonesIcon, UserPlus, Building2, Navigation
  };

  const getIcon = (iconName: string) => {
    return iconMap[iconName] || Mail;
  };

  // Filter FAQs
  const filteredFAQs = useMemo(() => {
    return faqs.filter(faq => {
      const matchesCategory = faqCategory === 'all' || faq.category === faqCategory;
      const matchesSearch = faqSearch === '' ||
        faq.question.en.toLowerCase().includes(faqSearch.toLowerCase()) ||
        faq.question.ar.includes(faqSearch) ||
        faq.answer.en.toLowerCase().includes(faqSearch.toLowerCase()) ||
        faq.answer.ar.includes(faqSearch);
      return matchesCategory && matchesSearch;
    }).sort((a, b) => (b.priority || 0) - (a.priority || 0));
  }, [faqCategory, faqSearch]);

  // Filter branches
  const filteredBranches = selectedBranch === 'all'
    ? branches
    : branches.filter(b => b.id === selectedBranch);

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));

    toast({
      title: isRTL ? '✓ تم الإرسال بنجاح' : '✓ Message Sent Successfully',
      description: isRTL 
        ? 'شكراً لتواصلك! سنرد عليك في أقرب وقت ممكن.'
        : 'Thank you for contacting us! We\'ll get back to you as soon as possible.',
    });

    setIsSubmitting(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      branch: '',
      message: '',
      newsletter: false
    });
  };

  // FAQ Categories
  const faqCategories = [
    { value: 'all', label: { en: 'All Questions', ar: 'جميع الأسئلة' } },
    { value: 'registration', label: { en: 'Registration', ar: 'التسجيل' } },
    { value: 'programs', label: { en: 'Programs', ar: 'البرامج' } },
    { value: 'fees', label: { en: 'Fees & Payment', ar: 'الرسوم والدفع' } },
    { value: 'facilities', label: { en: 'Facilities', ar: 'المرافق' } },
    { value: 'general', label: { en: 'General', ar: 'عام' } }
  ];

  // Statistics
  const stats = [
    {
      icon: Building2,
      value: contactStats.branches.toString(),
      label: { en: 'Branches', ar: 'فروع' },
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Clock,
      value: contactStats.responseTime,
      label: { en: 'Response Time', ar: 'وقت الرد' },
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Shield,
      value: contactStats.availability,
      label: { en: 'Emergency Line', ar: 'خط الطوارئ' },
      color: 'from-red-500 to-rose-500'
    },
    {
      icon: Globe,
      value: contactStats.languages.length.toString(),
      label: { en: 'Languages', ar: 'لغات' },
      color: 'from-purple-500 to-pink-500'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Enhanced Hero */}
      <PageHero
        title={isRTL ? 'تواصل معنا' : 'Contact Us'}
        subtitle={isRTL
          ? 'نحن هنا للإجابة على أسئلتك ومساعدتك - تواصل معنا عبر أي وسيلة تناسبك'
          : 'We\'re here to answer your questions and help you - reach out via any method that suits you'
        }
        breadcrumbs={[
          { label: t('common.home'), path: '/' },
          { label: t('nav.contact') }
        ]}
      />

      {/* Quick Stats */}
      <section className="py-12 bg-gradient-to-b from-background to-surface-1">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="p-6 rounded-2xl bg-card border-2 border-border hover:border-primary/30 shadow-xl text-center transition-all"
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} bg-opacity-10 mb-3`}>
                  <stat.icon className="h-6 w-6 text-primary" />
                </div>
                <div className="text-2xl font-bold gradient-text mb-1">{stat.value}</div>
                <div className="text-xs text-muted-foreground">
                  {stat.label[isRTL ? 'ar' : 'en']}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Contact Banner */}
      <section className="py-4 bg-gradient-to-r from-red-500/10 via-orange-500/10 to-red-500/10 border-y border-red-500/20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center justify-center gap-3 text-center flex-wrap"
          >
            <AlertCircle className="h-5 w-5 text-red-500" />
            <span className="text-sm font-semibold">
              {isRTL ? '🚨 طوارئ 24/7:' : '🚨 24/7 Emergency:'}
            </span>
            <a href={`tel:${emergencyContact.phone}`} className="text-sm font-bold text-red-500 hover:underline">
              {emergencyContact.phone}
            </a>
          </motion.div>
        </div>
      </section>

      {/* Primary Contact Methods */}
      <section className="py-20 bg-surface-1">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Zap className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {isRTL ? 'تواصل سريع' : 'Quick Contact'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'كيف يمكننا مساعدتك؟' : 'How Can We Help You?'}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {contactMethods.map((method, idx) => {
              const Icon = getIcon(method.icon);
              
              return (
                <motion.a
                  key={method.id}
                  href={method.href}
                  target={method.href.startsWith('http') ? '_blank' : undefined}
                  rel={method.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  whileHover={{ y: -8, scale: 1.03 }}
                  className={`p-8 rounded-3xl ${
                    method.primary 
                      ? 'bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border-2 border-primary/30'
                      : 'bg-card border-2 border-border'
                  } hover:border-primary/50 shadow-xl hover:shadow-2xl transition-all duration-500 text-center group relative overflow-hidden`}
                >
                  {/* Background Glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  <div className="relative z-10">
                    <motion.div
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.5 }}
                      className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-6"
                    >
                      <Icon className="h-8 w-8 text-primary" />
                    </motion.div>

                    <h3 className="text-lg font-bold mb-2 group-hover:text-primary transition-colors">
                      {method.title[isRTL ? 'ar' : 'en']}
                    </h3>

                    <p className="text-muted-foreground text-sm mb-3">
                      {method.value}
                    </p>

                    {method.description && (
                      <p className="text-xs text-muted-foreground mb-3">
                        {method.description[isRTL ? 'ar' : 'en']}
                      </p>
                    )}

                    {method.responseTime && (
                      <div className="flex items-center justify-center gap-2 text-xs">
                        <Clock className="h-3 w-3 text-green-500" />
                        <span className="text-green-500 font-semibold">
                          {method.responseTime[isRTL ? 'ar' : 'en']}
                        </span>
                      </div>
                    )}

                    {method.primary && (
                      <div className="absolute top-3 right-3">
                        <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                      </div>
                    )}
                  </div>
                </motion.a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Departments */}
      <section className="py-20 bg-gradient-to-b from-background to-surface-1">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl sm:text-5xl font-bold mb-6">
              {isRTL ? 'اتصل بالقسم المناسب' : 'Contact the Right Department'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {isRTL
                ? 'تواصل مباشرة مع القسم المختص للحصول على إجابات أسرع'
                : 'Contact the specialized department directly for faster answers'
              }
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {departments.map((dept, idx) => {
              const Icon = getIcon(dept.icon);
              
              return (
                <motion.div
                  key={dept.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  whileHover={{ y: -8 }}
                  className="p-6 rounded-2xl bg-card border-2 border-border hover:border-primary/30 shadow-xl transition-all"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 flex-shrink-0">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold mb-1">
                        {dept.name[isRTL ? 'ar' : 'en']}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {dept.description[isRTL ? 'ar' : 'en']}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <a
                      href={`mailto:${dept.email}`}
                      className="flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <Mail className="h-4 w-4" />
                      {dept.email}
                    </a>
                    {dept.phone && (
                      <a
                        href={`tel:${dept.phone}`}
                        className="flex items-center gap-2 text-sm text-primary hover:underline"
                      >
                        <Phone className="h-4 w-4" />
                        {dept.phone}
                      </a>
                    )}
                    {dept.contactPerson && (
                      <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                        {isRTL ? 'المسؤول:' : 'Contact:'} {dept.contactPerson[isRTL ? 'ar' : 'en']}
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 bg-surface-1">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-card border-2 border-border rounded-3xl p-8 shadow-2xl"
            >
              <div className="mb-8">
                <h2 className="text-3xl font-bold mb-3 gradient-text">
                  {isRTL ? 'أرسل لنا رسالة' : 'Send Us a Message'}
                </h2>
                <p className="text-muted-foreground">
                  {isRTL
                    ? 'املأ النموذج وسنرد عليك في أقرب وقت ممكن'
                    : 'Fill out the form and we\'ll get back to you as soon as possible'
                  }
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">
                      {isRTL ? 'الاسم' : 'Name'} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="mt-2"
                      placeholder={isRTL ? 'اسمك الكامل' : 'Your full name'}
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">
                      {isRTL ? 'البريد الإلكتروني' : 'Email'} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="mt-2"
                      placeholder={isRTL ? 'بريدك الإلكتروني' : 'Your email'}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone">
                    {isRTL ? 'رقم الهاتف' : 'Phone'} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="mt-2"
                    placeholder={isRTL ? 'رقم هاتفك' : 'Your phone number'}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="subject">
                      {isRTL ? 'الموضوع' : 'Subject'} <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      required
                      value={formData.subject}
                      onValueChange={(value) => setFormData({...formData, subject: value})}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder={isRTL ? 'اختر الموضوع' : 'Select subject'} />
                      </SelectTrigger>
                      <SelectContent>
                        {contactSubjects.map(subject => (
                          <SelectItem key={subject.value} value={subject.value}>
                            {subject.label[isRTL ? 'ar' : 'en']}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="branch">
                      {isRTL ? 'الفرع (اختياري)' : 'Branch (Optional)'}
                    </Label>
                    <Select
                      value={formData.branch}
                      onValueChange={(value) => setFormData({...formData, branch: value})}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder={isRTL ? 'اختر الفرع' : 'Select branch'} />
                      </SelectTrigger>
                      <SelectContent>
                        {branches.map(branch => (
                          <SelectItem key={branch.id} value={branch.id}>
                            {branch.name[isRTL ? 'ar' : 'en']}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="message">
                    {isRTL ? 'الرسالة' : 'Message'} <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="mt-2"
                    placeholder={isRTL ? 'اكتب رسالتك هنا...' : 'Write your message here...'}
                  />
                </div>

                <div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.newsletter}
                      onChange={(e) => setFormData({...formData, newsletter: e.target.checked})}
                      className="w-5 h-5 rounded border-border text-primary focus:ring-primary mt-0.5"
                    />
                    <span className="text-sm text-muted-foreground">
                      {isRTL
                        ? 'أرغب في تلقي تحديثات وأخبار الأكاديمية'
                        : 'I want to receive academy updates and news'
                      }
                    </span>
                  </label>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full btn-glow"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className={isRTL ? 'ml-2' : 'mr-2'}
                      >
                        ⏳
                      </motion.div>
                      {isRTL ? 'جاري الإرسال...' : 'Sending...'}
                    </>
                  ) : (
                    <>
                      <Send className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      {isRTL ? 'إرسال الرسالة' : 'Send Message'}
                    </>
                  )}
                </Button>
              </form>
            </motion.div>

            {/* Right Side Info */}
            <motion.div
              initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              {/* Operating Hours */}
              <div className="bg-card border-2 border-border rounded-2xl p-8 shadow-xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10">
                    <Clock className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold">
                    {isRTL ? 'ساعات العمل' : 'Operating Hours'}
                  </h3>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 rounded-xl bg-surface-1">
                    <span className="font-semibold">{isRTL ? 'السبت - الخميس' : 'Saturday - Thursday'}</span>
                    <span className="text-primary font-bold">8:00 AM - 10:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center p-3 rounded-xl bg-surface-1">
                    <span className="font-semibold">{isRTL ? 'الجمعة' : 'Friday'}</span>
                    <span className="text-primary font-bold">2:00 PM - 10:00 PM</span>
                  </div>
                </div>

                <div className="mt-6 p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                  <div className="flex items-center gap-2 text-green-500 font-semibold">
                    <CheckCircle2 className="h-5 w-5" />
                    <span>{isRTL ? '✓ مفتوح الآن' : '✓ Open Now'}</span>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="bg-card border-2 border-border rounded-2xl p-8 shadow-xl">
                <h3 className="text-xl font-bold mb-6">
                  {isRTL ? 'تابعنا على وسائل التواصل' : 'Follow Us on Social Media'}
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  {socialLinks.map((social, idx) => (
                    <motion.a
                      key={idx}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.1, y: -5 }}
                      whileTap={{ scale: 0.95 }}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl bg-surface-1 hover:bg-primary/10 border border-border hover:border-primary/30 transition-all"
                    >
                      <social.icon className="h-6 w-6 text-primary" />
                      <span className="text-xs font-medium">{social.platform}</span>
                    </motion.a>
                  ))}
                </div>
              </div>

              {/* Quick Info */}
              <div className="bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border-2 border-primary/20 rounded-2xl p-8 shadow-xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold">
                    {isRTL ? 'المقر الرئيسي' : 'Main Campus'}
                  </h3>
                </div>

                <p className="text-muted-foreground mb-4">
                  {branches.find(b => b.main)?.address[isRTL ? 'ar' : 'en']}
                </p>

                <div className="space-y-2">
                  <a
                    href={`tel:${branches.find(b => b.main)?.phone}`}
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <Phone className="h-4 w-4" />
                    {branches.find(b => b.main)?.phone}
                  </a>
                  <a
                    href={`mailto:${branches.find(b => b.main)?.email}`}
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <Mail className="h-4 w-4" />
                    {branches.find(b => b.main)?.email}
                  </a>
                </div>

                <Button
                  variant="outline"
                  className="w-full mt-6"
                  onClick={() => document.getElementById('branches')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  <Navigation className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {isRTL ? 'شاهد جميع الفروع' : 'View All Branches'}
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Branches Section */}
      <section id="branches" className="py-20 bg-gradient-to-b from-background to-surface-1">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-8">
              <Building2 className="h-5 w-5 text-primary" />
              <span className="text-sm font-semibold text-primary">
                {branches.length} {isRTL ? 'فروع' : 'Branches'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'فروعنا في جميع أنحاء غزة' : 'Our Branches Across Gaza'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
              {isRTL
                ? 'اختر الفرع الأقرب إليك - جميع فروعنا مجهزة بمرافق عالمية المستوى'
                : 'Choose the branch nearest to you - all our branches are equipped with world-class facilities'
              }
            </p>

            {/* Branch Filter */}
            <div className="flex justify-center">
              <Select
                value={selectedBranch}
                onValueChange={setSelectedBranch}
              >
                <SelectTrigger className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'جميع الفروع' : 'All Branches'}</SelectItem>
                  {branches.map(branch => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name[isRTL ? 'ar' : 'en']}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredBranches.map((branch, idx) => (
              <BranchCard key={branch.id} branch={branch} isRTL={isRTL} delay={idx * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-surface-1">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-yellow-500/10 border border-yellow-500/20 mb-8">
              <FileText className="h-5 w-5 text-yellow-500" />
              <span className="text-sm font-semibold text-yellow-500">
                {isRTL ? 'إجابات سريعة' : 'Quick Answers'}
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6 gradient-text">
              {isRTL ? 'الأسئلة الشائعة' : 'Frequently Asked Questions'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {isRTL
                ? 'ابحث عن إجابات للأسئلة الأكثر شيوعاً - قد تجد إجابتك هنا!'
                : 'Find answers to the most common questions - you might find your answer here!'
              }
            </p>
          </motion.div>

          {/* FAQ Filters */}
          <div className="max-w-4xl mx-auto mb-8 space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'right-4' : 'left-4'} h-5 w-5 text-muted-foreground`} />
              <Input
                type="text"
                placeholder={isRTL ? 'ابحث في الأسئلة...' : 'Search questions...'}
                value={faqSearch}
                onChange={(e) => setFaqSearch(e.target.value)}
                className={`${isRTL ? 'pr-12' : 'pl-12'} h-12`}
              />
            </div>

            {/* Category Tabs */}
            <div className="flex justify-center gap-2 flex-wrap">
              {faqCategories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setFaqCategory(cat.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    faqCategory === cat.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card border border-border hover:border-primary/50'
                  }`}
                >
                  {cat.label[isRTL ? 'ar' : 'en']}
                </button>
              ))}
            </div>

            {/* Results Count */}
            <div className="text-center text-sm text-muted-foreground">
              {isRTL 
                ? `عرض ${filteredFAQs.length} من ${faqs.length} سؤال`
                : `Showing ${filteredFAQs.length} of ${faqs.length} questions`
              }
            </div>
          </div>

          {/* FAQ Accordion */}
          <div className="max-w-4xl mx-auto">
            <AnimatePresence mode="wait">
              {filteredFAQs.length > 0 ? (
                <motion.div
                  key={`${faqCategory}-${faqSearch}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Accordion type="single" collapsible className="space-y-4">
                    {filteredFAQs.map((faq, idx) => (
                      <motion.div
                        key={faq.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                      >
                        <AccordionItem
                          value={faq.id}
                          className="bg-card border-2 border-border rounded-2xl px-6 hover:border-primary/30 transition-all"
                        >
                          <AccordionTrigger className="text-left hover:no-underline py-6">
                            <div className="flex items-start gap-3 flex-1">
                              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 flex-shrink-0 mt-1">
                                <span className="text-primary font-bold text-sm">{idx + 1}</span>
                              </div>
                              <span className="font-semibold text-lg pr-4">
                                {faq.question[isRTL ? 'ar' : 'en']}
                              </span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="text-muted-foreground pb-6 pt-2 pl-11 leading-relaxed">
                            {faq.answer[isRTL ? 'ar' : 'en']}
                          </AccordionContent>
                        </AccordionItem>
                      </motion.div>
                    ))}
                  </Accordion>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-20"
                >
                  <Search className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <p className="text-lg text-muted-foreground">
                    {isRTL ? 'لم يتم العثور على أسئلة' : 'No questions found'}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Still Have Questions CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 max-w-2xl mx-auto text-center p-8 rounded-3xl bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border-2 border-primary/20"
          >
            <h3 className="text-2xl font-bold mb-4">
              {isRTL ? 'لم تجد إجابتك؟' : 'Didn\'t Find Your Answer?'}
            </h3>
            <p className="text-muted-foreground mb-6">
              {isRTL
                ? 'لا تتردد في التواصل معنا - فريقنا جاهز لمساعدتك!'
                : 'Don\'t hesitate to contact us - our team is ready to help!'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                <MessageCircle className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'تواصل معنا' : 'Contact Us'}
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => window.open('https://wa.me/970591234567', '_blank')}
              >
                <MessageCircle className={`h-5 w-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'دردشة واتساب' : 'WhatsApp Chat'}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// Branch Card Component
const BranchCard = ({ 
  branch, 
  isRTL,
  delay = 0
}: { 
  branch: Branch; 
  isRTL: boolean;
  delay?: number;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay }}
      whileHover={{ y: -8 }}
      className={`bg-card border-2 ${branch.main ? 'border-primary/50' : 'border-border'} rounded-2xl overflow-hidden hover:border-primary/30 shadow-xl hover:shadow-2xl transition-all duration-500 relative`}
    >
      {/* Main Badge */}
      {branch.main && (
        <div className="absolute top-4 right-4 z-10">
          <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-bold">
            <Star className="h-3 w-3 fill-current" />
            {isRTL ? 'رئيسي' : 'MAIN'}
          </div>
        </div>
      )}

      {/* Image */}
      {branch.image && (
        <div className="aspect-video bg-muted overflow-hidden">
          <img
            src={branch.image}
            alt={branch.name[isRTL ? 'ar' : 'en']}
            className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
          />
        </div>
      )}

      {/* Content */}
      <div className="p-6">
        <h3 className="text-xl font-bold mb-3">
          {branch.name[isRTL ? 'ar' : 'en']}
        </h3>

        <div className="space-y-3 mb-4">
          <div className="flex items-start gap-2 text-sm">
            <MapPin className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
            <span className="text-muted-foreground">
              {branch.address[isRTL ? 'ar' : 'en']}
            </span>
          </div>

          <a
            href={`tel:${branch.phone}`}
            className="flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <Phone className="h-4 w-4" />
            {branch.phone}
          </a>

          <a
            href={`mailto:${branch.email}`}
            className="flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <Mail className="h-4 w-4" />
            {branch.email}
          </a>

          <div className="flex items-start gap-2 text-sm">
            <Clock className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-muted-foreground">{branch.hours.weekdays}</div>
              <div className="text-xs text-muted-foreground">
                {isRTL ? 'الجمعة:' : 'Friday:'} {branch.hours.friday}
              </div>
            </div>
          </div>
        </div>

        {/* Sports Available */}
        {branch.sports && branch.sports.length > 0 && (
          <div className="pt-4 border-t border-border">
            <div className="text-xs text-muted-foreground mb-2">
              {isRTL ? 'الرياضات المتوفرة:' : 'Available Sports:'}
            </div>
            <div className="flex flex-wrap gap-2">
              {branch.sports.slice(0, 3).map((sport, idx) => (
                <span
                  key={idx}
                  className="px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium"
                >
                  {sport}
                </span>
              ))}
              {branch.sports.length > 3 && (
                <span className="px-2 py-1 rounded-full bg-muted text-muted-foreground text-xs font-medium">
                  +{branch.sports.length - 3}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Manager */}
        {branch.manager && (
          <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
            {isRTL ? 'المدير:' : 'Manager:'} {branch.manager[isRTL ? 'ar' : 'en']}
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            onClick={() => window.open(`tel:${branch.phone}`)}
          >
            <Phone className="h-4 w-4 mr-2" />
            {isRTL ? 'اتصل' : 'Call'}
          </Button>
          {branch.coordinates && (
            <Button
              size="sm"
              variant="outline"
              className="flex-1"
              onClick={() => window.open(`https://maps.google.com/?q=${branch.coordinates!.lat},${branch.coordinates!.lng}`, '_blank')}
            >
              <Navigation className="h-4 w-4 mr-2" />
              {isRTL ? 'موقع' : 'Map'}
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Contact;
